import { useRef, useState } from "react";
import { AlertCircle, BookOpen, CheckCircle2, Loader2, X } from "lucide-react";

import {
  LOGBOOK_TERMS_INTRO,
  LOGBOOK_TERMS_SECTIONS,
  TRAINING_CODES,
} from "../constants/logbookTerms";
import { useAcceptLogbookTermsMutation } from "../api/logbookApi";

/**
 * The terms of the Logbook Training and Mentoring Scheme.
 *
 * Three things share one copy of the document, which is the whole reason it is
 * shaped this way:
 *
 *   LogbookTermsDocument — the text itself, and nothing else.
 *   LogbookTermsGate     — that text with an acceptance footer, standing in
 *                          front of a logbook that has not been accepted yet.
 *   LogbookTermsModal    — that text again, reopenable at any time afterwards.
 *
 * The rules here decide what must be recorded, how often it must be signed and
 * what fails a stage, so they are not a one-time hurdle: a candidate in week
 * thirty needs to check 4.14 without hunting for a PDF. Accepting must not be
 * the last time they can read what they accepted.
 */

/* ------------------------------------------------------------------ */
/* The document                                                         */
/* ------------------------------------------------------------------ */
export function LogbookTermsDocument({
  scrollRef,
  onScroll,
  endNote,
  className = "max-h-[60vh]",
}: {
  scrollRef?: React.RefObject<HTMLDivElement>;
  onScroll?: () => void;
  /** The gate shows this to confirm the reader reached the bottom. */
  endNote?: boolean;
  className?: string;
}) {
  return (
    <div ref={scrollRef} onScroll={onScroll} className={`overflow-y-auto px-6 py-5 ${className}`}>
      <h2 className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
        1.&nbsp;&nbsp;Introduction
      </h2>
      <div className="mt-2 space-y-3">
        {LOGBOOK_TERMS_INTRO.map((p, i) => (
          <p key={i} className="text-[13px] leading-relaxed text-gray-700">
            {p}
          </p>
        ))}
      </div>

      {LOGBOOK_TERMS_SECTIONS.map((section) => (
        <section key={section.title} className="mt-6">
          <h2 className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            {section.title}
          </h2>
          <dl className="mt-2 space-y-2.5">
            {section.clauses.map((c, i) => (
              <div key={c.ref ?? i} className="flex gap-3">
                {c.ref ? (
                  <dt className="w-14 shrink-0 text-[13px] font-semibold tabular-nums text-gray-500">
                    {c.ref}
                  </dt>
                ) : null}
                <dd className="text-[13px] leading-relaxed text-gray-700">{c.text}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}

      <section className="mt-6">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
          Training codes
        </h2>
        <p className="mt-1 text-[12px] text-gray-500">
          Every diary entry must carry the training code for the activity it records.
        </p>
        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-gray-50">
                <th className="w-12 px-3 py-2 text-[11px] font-semibold text-gray-500">S/N</th>
                <th className="w-28 px-3 py-2 text-[11px] font-semibold text-gray-500">Code</th>
                <th className="px-3 py-2 text-[11px] font-semibold text-gray-500">Description</th>
              </tr>
            </thead>
            <tbody>
              {TRAINING_CODES.map((row, i) => (
                <tr
                  key={row.code}
                  className={`border-t border-gray-100 ${row.heading ? "bg-gray-50" : ""}`}
                >
                  <td className="px-3 py-1.5 text-[12px] tabular-nums text-gray-400">{i + 1}</td>
                  <td
                    className={`px-3 py-1.5 text-[12px] ${
                      row.heading ? "font-bold text-gray-900" : "font-medium text-gray-700"
                    }`}
                  >
                    {row.code}
                  </td>
                  <td
                    className={`px-3 py-1.5 text-[12px] ${
                      row.heading ? "font-bold text-gray-900" : "text-gray-700"
                    }`}
                  >
                    {row.description || <span className="text-gray-300">— to be specified —</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {endNote ? (
        <p className="mt-6 flex items-center gap-1.5 text-[12px] font-medium text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          You have reached the end of the terms.
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Read it again                                                        */
/* ------------------------------------------------------------------ */
function fmtDate(iso?: string | null) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

/** The same document, reopened read-only once it has been accepted. */
export function LogbookTermsModal({
  open,
  onClose,
  acceptedAt,
}: {
  open: boolean;
  onClose: () => void;
  acceptedAt?: string | null;
}) {
  if (!open) return null;

  const accepted = fmtDate(acceptedAt);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b bg-indigo-50 px-6 py-4">
          <div className="flex items-start gap-3">
            <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
            <div>
              <h2 className="font-bold text-gray-900">
                NIQS Logbook Training and Mentoring Scheme
              </h2>
              {/* Stated rather than implied: this is a record of something the
                  candidate already agreed to, not a fresh demand. */}
              <p className="mt-0.5 text-xs text-gray-600">
                {accepted
                  ? `You accepted these terms on ${accepted}.`
                  : "The terms you accepted for this logbook."}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-white/60 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <LogbookTermsDocument className="flex-1" />

        <footer className="flex justify-end border-t bg-gray-50 px-6 py-3">
          <button
            onClick={onClose}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The gate                                                             */
/* ------------------------------------------------------------------ */

/**
 * Stands in front of the whole page rather than sitting on it as a dismissible
 * modal: reading the rules is the first piece of work, not an interruption to
 * it.
 *
 * The checkbox stays disabled until the document has actually been scrolled to
 * the end. A small friction, and the point: an acceptance is stored against
 * this logbook with the date and the edition, and it should mean the candidate
 * reached the bottom of what they agreed to.
 */
export default function LogbookTermsGate({
  logbookId,
  stage,
  onAccepted,
}: {
  logbookId: string;
  stage?: number | string | null;
  /** Refetch, so the page re-renders un-gated. */
  onAccepted: () => void | Promise<any>;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [readToEnd, setReadToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [accept, { isLoading }] = useAcceptLogbookTermsMutation();

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    // A tolerance, because a fractional scrollHeight at some zoom levels never
    // reaches an exact equality and the checkbox would never unlock.
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReadToEnd(true);
  };

  const submit = async () => {
    setErr(null);
    try {
      await accept({ logbookId }).unwrap();
      await onAccepted();
    } catch (e: any) {
      setErr(
        e?.data?.message || e?.message || "Could not record your acceptance. Please try again.",
      );
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="overflow-hidden rounded-xl border bg-white">
        <header className="flex items-start gap-3 border-b bg-indigo-50 px-6 py-5">
          <BookOpen className="mt-0.5 h-6 w-6 shrink-0 text-indigo-600" />
          <div>
            <h1 className="text-lg font-bold text-gray-900">
              NIQS Logbook Training and Mentoring Scheme
            </h1>
            <p className="mt-0.5 text-sm text-gray-600">
              Read and accept these terms to open{" "}
              {stage ? `your Stage ${stage} logbook` : "this logbook"}. Acceptance is recorded
              against this logbook, and you can reopen the terms at any time afterwards.
            </p>
          </div>
        </header>

        <LogbookTermsDocument scrollRef={scrollRef} onScroll={onScroll} endNote />

        <footer className="space-y-3 border-t bg-gray-50 px-6 py-4">
          {err ? (
            <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <p className="text-xs text-rose-800">{err}</p>
            </div>
          ) : null}

          <label
            className={`flex items-start gap-2.5 ${
              readToEnd ? "cursor-pointer" : "cursor-not-allowed opacity-60"
            }`}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0"
              checked={agreed}
              disabled={!readToEnd}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span className="text-[13px] text-gray-700">
              I have read the terms of the NIQS Logbook Training and Mentoring Scheme and agree to
              keep this logbook and its diary in accordance with them.
            </span>
          </label>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-gray-500">
              {readToEnd
                ? "Your acceptance will be recorded with today's date."
                : "Scroll to the end of the terms to continue."}
            </p>
            <button
              type="button"
              onClick={submit}
              disabled={!agreed || isLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isLoading ? "Recording…" : "Accept and open logbook"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
